'use client'

import Image from 'next/image'
import Link from 'next/link'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { cn } from '@/utilities/ui'

export type EfficientDesignProjectCard = {
  id: number
  title: string
  imageUrl: string
  imageAlt?: string | null
  href: string | null
}

type Props = {
  videoUrl: string
  isVideoMime: boolean
  videoPosterAlt?: string | null
  heading: string
  subHeading: string
  projectHeading: string
  projects: EfficientDesignProjectCard[]
  className?: string
}

/** Matches design: tan → cool grey, 265deg */
const GRADIENT = 'linear-gradient(265deg, #C4AD86 37%, #DBDBDB 100%)'

function ProjectCard({ item }: { item: EfficientDesignProjectCard }) {
  const inner = (
    <div
      className={cn(
        'flex gap-3 2xl:h-[145px] overflow-hidden rounded-lg bg-[#E1D6C3] shadow-sm transition-transform duration-300 ease-out hover:scale-[1.05]',
        'md:gap-3.5 ',
      )}
    >
      <div className="relative h-32 w-32 shrink-0 overflow-hidden bg-[#E2D7C6] md:h-35  2xl:h-[145px] md:w-35 2xl:max-h-[145px]">
        <Image alt={item.imageAlt || item.title} className="object-cover" fill src={item.imageUrl} />
      </div>
      <div className="flex-1 flex justify-start">
        <div className="flex w-[83%] flex-col justify-end pb-[20px] md:pb-[30px]">
          <span className="text-left font-medium leading-snug tracking-tight text-[#1a1a1a] text-[20px]">
            {item.title}
          </span>
          <span className="mt-1 block h-1.5 w-full bg-[#1595a7]" aria-hidden />
        </div>
      </div>
    </div>
  )

  if (!item.href) {
    return <div className="block">{inner}</div>
  }

  const external = /^https?:\/\//i.test(item.href)
  if (external) {
    return (
      <a
        className="block text-inherit no-underline"
        href={item.href}
        rel="noopener noreferrer"
        target="_blank"
      >
        {inner}
      </a>
    )
  }

  return (
    <Link className="block text-inherit no-underline" href={item.href}>
      {inner}
    </Link>
  )
}

export const HomepageEfficientDesign: React.FC<Props> = ({
  videoUrl,
  isVideoMime,
  videoPosterAlt,
  heading,
  subHeading,
  projectHeading,
  projects,
  className,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [muted, setMuted] = useState(true)
  // Defer the (multi-MB) video file until the section scrolls near the viewport.
  const [videoInView, setVideoInView] = useState(false)

  useEffect(() => {
    if (!isVideoMime || videoInView) return
    const el = videoRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVideoInView(true)
          observer.disconnect()
        }
      },
      { rootMargin: '300px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [isVideoMime, videoInView])

  const toggleSound = useCallback(() => {
    const el = videoRef.current
    if (!el) return
    const next = !el.muted
    el.muted = next
    setMuted(next)
    if (!next) {
      void el.play().catch(() => {})
    }
  }, [])

  return (
    <section className={cn('w-full font-sans antialiased', className)}>
      {/* 40% media | 60% content (desktop) */}
      <div
        className="grid grid-cols-1 gap-[20px] p-[20px] md:grid-cols-[37fr_63fr] md:items-stretch"
        style={{ background: GRADIENT, filter: 'drop-shadow(0px 0px 10px rgba(0,0,0,0.5))' }}
      >
        <div className="relative w-full min-w-0 bg-transparent h-[86vh] md:col-start-1 md:row-start-1 md:my-auto">
          {/* 20px space on all sides of the media */}
          <div className="box-border flex h-full min-h-0 w-full flex-col justify-center">
            <div className="relative min-h-0 w-full flex-1 h-[86vh] md:my-auto mx-auto overflow-hidden shadow-[0px_0px_10px_0px_rgba(0,0,0,0.5)]">
              {isVideoMime ? (
                <video
                  ref={videoRef}
                  autoPlay
                  className="absolute inset-0 box-border h-full w-full object-cover object-center"
                  loop
                  muted={muted}
                  playsInline
                  preload="none"
                  {...(videoInView ? { src: videoUrl } : {})}
                />
              ) : (
                <Image
                  alt={videoPosterAlt?.trim() || ''}
                  className="object-cover object-center"
                  fill
                  priority
                  sizes="(max-width: 1024px) 100vw, 40vw"
                  src={videoUrl}
                />
              )}

              {isVideoMime ? (
                <div className="absolute bottom-[10px] left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-1">
                  <button
                    type="button"
                    aria-label={muted ? 'Unmute video' : 'Mute video'}
                    aria-pressed={!muted}
                    className="inline-flex flex-col items-center gap-1.5 text-[15px] font-[500] leading-[18px] tracking-[5.4px] transition"
                    style={{ color: 'rgba(255, 255, 255, 0.6)' }}
                    onClick={toggleSound}
                  >
                    {muted ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 576 512"
                        fill="currentColor"
                        aria-hidden
                        style={{
                          display: 'block',
                          width: '32px',
                          height: '29px',
                          color: 'white',
                          marginBottom: '20px',
                          flexShrink: 0,
                        }}
                      >
                        <path d="M48 352l48 0 134.1 119.2c6.4 5.7 14.6 8.8 23.1 8.8 19.2 0 34.8-15.6 34.8-34.8l0-378.4c0-19.2-15.6-34.8-34.8-34.8-8.5 0-16.7 3.1-23.1 8.8L96 160 48 160c-26.5 0-48 21.5-48 48l0 96c0 26.5 21.5 48 48 48zM367 175c-9.4 9.4-9.4 24.6 0 33.9l47 47-47 47c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l47-47 47 47c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-47-47 47-47c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-47 47-47-47c-9.4-9.4-24.6-9.4-33.9 0z" />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 640 512"
                        fill="currentColor"
                        aria-hidden
                        style={{
                          display: 'block',
                          width: '32px',
                          height: '29px',
                          color: 'white',
                          marginBottom: '20px',
                          flexShrink: 0,
                        }}
                      >
                        <path d="M533.6 32.5c-10.3-8.4-25.4-6.8-33.8 3.5s-6.8 25.4 3.5 33.8C557.5 113.8 592 180.8 592 256s-34.5 142.2-88.7 186.3c-10.3 8.4-11.8 23.5-3.5 33.8s23.5 11.8 33.8 3.5C598.5 426.7 640 346.2 640 256S598.5 85.2 533.6 32.5zM473.1 107c-10.3-8.4-25.4-6.8-33.8 3.5s-6.8 25.4 3.5 33.8C475.3 170.7 496 210.9 496 256s-20.7 85.3-53.2 111.8c-10.3 8.4-11.8 23.5-3.5 33.8s23.5 11.8 33.8 3.5c43.2-35.2 70.9-88.9 70.9-149s-27.7-113.8-70.9-149zm-60.5 74.5c-10.3-8.4-25.4-6.8-33.8 3.5s-6.8 25.4 3.5 33.8C393.1 227.6 400 241 400 256s-6.9 28.4-17.7 37.3c-10.3 8.4-11.8 23.5-3.5 33.8s23.5 11.8 33.8 3.5C434.1 312.9 448 286.1 448 256s-13.9-56.9-35.4-74.5zM80 352l48 0 134.1 119.2c6.4 5.7 14.6 8.8 23.1 8.8 19.2 0 34.8-15.6 34.8-34.8l0-378.4c0-19.2-15.6-34.8-34.8-34.8-8.5 0-16.7 3.1-23.1 8.8L128 160 80 160c-26.5 0-48 21.5-48 48l0 96c0 26.5 21.5 48 48 48z" />
                      </svg>
                    )}
                    <span className="whitespace-nowrap">
                      {muted ? 'Click To Listen' : 'Sound on'}
                    </span>
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div
          className={cn(
            'grid min-h-0 w-full min-w-0 grid-rows-[auto_auto] gap-8',
            'md:col-start-2 md:row-start-1 md:flex md:h-full md:min-h-0 md:flex-col md:gap-0 md:p-0',
          )}
        >
          {/* Top: heading + sub-heading (fills space above projects; centered vertically) */}
          <div className="flex min-h-0 flex-col justify-center md:flex-1 md:min-h-0 md:mb-[34px]">
            <h2
              className="text-left text-[6vh] leading-[1.2em] text-white lg:whitespace-nowrap"
              style={{ fontFamily: 'var(--font-outfit-family), sans-serif', fontWeight: 100 }}
            >
              {heading}
            </h2>
            <p className="text-left text-base font-extralight italic text-white/95 ">
              {subHeading}
            </p>
          </div>

          {/* Bottom: projects */}
          <div className="flex min-h-0 shrink-0 flex-col">
            <h3 className="text-left text-lg font-thin leading-snug text-white text-[32px]">
              {projectHeading}
            </h3>

            {projects.length > 0 ? (
              <ul className="mt-4 flex flex-col gap-[30px] min-[1440px]:grid min-[1440px]:grid-cols-2 ">
                {projects.map((item) => (
                  <li key={item.id} className="min-w-0 w-full">
                    <ProjectCard item={item} />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-4 text-sm text-white/75 lg:mt-4">
                Select projects in the CMS to show cards here.
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
